import {
  canvasMeasureFont,
  layoutTextBlock,
  normRectWidthPx,
  snapSurfFontWeight,
  type TextLayoutContext,
  type TextMeasurer,
} from "@vida/surf-core";

const measureCanvas =
  typeof document !== "undefined" ? document.createElement("canvas") : null;
let measureCtx: CanvasRenderingContext2D | null = null;

function getMeasureCtx(): CanvasRenderingContext2D | null {
  if (!measureCanvas) return null;
  measureCtx ??= measureCanvas.getContext("2d");
  return measureCtx;
}

/** Canvas 2D measurer — authority for line breaks (canvas + PDF). */
export function createCanvasTextMeasurer(): TextMeasurer {
  return (text, fontFamily, fontWeight, fontSizePx) => {
    const ctx = getMeasureCtx();
    if (!ctx) return text.length * fontSizePx * 0.52;
    ctx.font = canvasMeasureFont(fontFamily, fontWeight, fontSizePx);
    return ctx.measureText(text).width;
  };
}

export function measureTextAscentPx(
  fontFamily: string,
  fontWeight: number,
  fontSizePx: number,
): number {
  const ctx = getMeasureCtx();
  if (!ctx) return fontSizePx * 0.8;
  ctx.font = canvasMeasureFont(fontFamily, fontWeight, fontSizePx);
  const metrics = ctx.measureText("Hg");
  return metrics.actualBoundingBoxAscent ?? fontSizePx * 0.8;
}

export async function ensureSurfFontsLoaded(
  specs: Array<{ font: string; weight: number }>,
): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) return;
  const unique = new Set<string>();
  for (const { font, weight } of specs) {
    unique.add(canvasMeasureFont(font, weight, 16));
  }
  await Promise.all([...unique].map((spec) => document.fonts.load(spec)));
  await document.fonts.ready;
}

export function createWebTextLayoutContext(slideHeightPx: number): TextLayoutContext {
  return { measurer: createCanvasTextMeasurer(), slideHeightPx };
}

export function layoutLayerTextLines(
  text: string,
  rectW: number,
  fontFamily: string,
  fontWeight: number,
  sizeRel: number,
  lineHeight: number,
  slideHeightPx: number,
  maxLines?: number,
  measurer: TextMeasurer = createCanvasTextMeasurer(),
): string[] {
  const fontSizePx = sizeRel * slideHeightPx;
  const maxWidthPx = normRectWidthPx(rectW, slideHeightPx);
  return layoutTextBlock(
    text,
    maxWidthPx,
    measurer,
    fontFamily,
    fontWeight,
    fontSizePx,
    lineHeight,
    { maxLines },
  ).lines;
}

export { snapSurfFontWeight };
